/**
 * VrCanvasPanel — ONE mesh, ONE canvas texture, redrawn only when its signature moves (XR P2 "VR UI / UX",
 * 2026-09-28). The world panel, the wrist menu and any later in-headset surface are one of these.
 *
 * The quad lives in RIG-LOCAL space (parented to the avatar rig, the frame three composes the XR camera and the
 * controllers in), so the FP64 floating origin can never move it and a ray from a rig-child controller is tested in
 * the same frame with no conversion. It draws on top of the world (depthTest off, a high renderOrder): a UI panel is
 * never occluded by the geometry it floats in front of. The hit test is the pure `rayHitRect` (front face only).
 */

import * as THREE from 'three/webgpu';
import { markMrtOverlay } from 'enginex/render/post/mrtOverlay.js';
import { rayHitRect, type Q4, type V3 } from '../../lib/xr_ui_input.js';
import { uvToCanvas } from '../../lib/xr_ui_layout.js';

export interface PanelHit { t: number; px: number; py: number; point: V3 }

export class VrCanvasPanel {
  readonly mesh: THREE.Mesh;
  readonly canvas: HTMLCanvasElement;
  readonly g: CanvasRenderingContext2D;
  private readonly tex: THREE.CanvasTexture;
  private readonly mat: THREE.MeshBasicMaterial;
  private sig = '';
  /** How many times the canvas was repainted (the "redraw only on change" law is judged by this counter). */
  paints = 0;

  constructor(readonly name: string, readonly wM: number, readonly hM: number, readonly cw: number, readonly ch: number, renderOrder: number) {
    this.canvas = document.createElement('canvas');
    this.canvas.width = cw;
    this.canvas.height = ch;
    this.g = this.canvas.getContext('2d')!;
    this.tex = new THREE.CanvasTexture(this.canvas);
    this.tex.colorSpace = THREE.SRGBColorSpace;
    this.tex.anisotropy = 4; // the panel is often seen at an angle (below the eye, tilted back)
    this.mat = new THREE.MeshBasicMaterial({ map: this.tex, transparent: true, depthTest: false, depthWrite: false, toneMapped: false });
    markMrtOverlay(this.mat); // THE OVERLAY LAW: a transparent overlay never rewrites the surface G-buffer under it
    this.mesh = new THREE.Mesh(new THREE.PlaneGeometry(wM, hM), this.mat);
    this.mesh.name = name;
    this.mesh.renderOrder = renderOrder;
    this.mesh.frustumCulled = false;
    this.mesh.visible = false;
  }

  get visible(): boolean { return this.mesh.visible; }
  set visible(v: boolean) { this.mesh.visible = v; }

  /** Repaint only when `sig` changed; returns whether it painted. */
  paint(sig: string, draw: (g: CanvasRenderingContext2D) => void): boolean {
    if (sig === this.sig) return false;
    this.sig = sig;
    this.g.clearRect(0, 0, this.cw, this.ch);
    draw(this.g);
    this.tex.needsUpdate = true;
    this.paints++;
    return true;
  }

  /** Force the next paint (a theme change, a font that just loaded). */
  invalidate(): void { this.sig = ''; }

  setPose(pos: V3, rot: Q4): void {
    this.mesh.position.set(pos.x, pos.y, pos.z);
    this.mesh.quaternion.set(rot.x, rot.y, rot.z, rot.w);
  }

  pos(): V3 { const p = this.mesh.position; return { x: p.x, y: p.y, z: p.z }; }
  rot(): Q4 { const q = this.mesh.quaternion; return { x: q.x, y: q.y, z: q.z, w: q.w }; }

  /** A rig-local ray against the visible panel: distance + canvas pixel, or null. */
  hit(o: V3, d: V3): PanelHit | null {
    if (!this.mesh.visible) return null;
    const r = rayHitRect(o, d, this.pos(), this.rot(), this.wM, this.hM);
    if (!r) return null;
    const { px, py } = uvToCanvas(r.u, r.v, this.cw, this.ch);
    return { t: r.t, px, py, point: r.point };
  }

  dispose(): void {
    this.mesh.removeFromParent();
    this.mesh.geometry.dispose();
    this.mat.dispose();
    this.tex.dispose();
  }
}
